import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeftIcon,
    CircleNotchIcon,
    SparkleIcon,
    CoinsIcon,
    GoogleLogoIcon,
    LightningIcon,
    EnvelopeSimpleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import LoadingOverlay from "@/components/LoadingOverlay";
import BuyCreditsCard from "@/components/BuyCreditsCard";
import EmailSignIn from "@/components/EmailSignIn";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import {
    clearPendingUpload,
    loadPendingUpload,
    savePendingUpload,
} from "@/lib/pendingUpload";

const PRESET_ID = "more_handsome";

export default function Upload() {
    const navigate = useNavigate();
    const { user, loading, refresh, startSignIn } = useAuth();
    const [file, setFile] = useState(null);
    const [working, setWorking] = useState(false);
    const [showEmailSignIn, setShowEmailSignIn] = useState(false);
    // Same-page email path: when verify-code succeeds we flip this on
    // synchronously, then a dedicated useEffect fires handleGenerate
    // once React has flushed setUser. No sessionStorage round-trip,
    // no race with the OAuth-return effect.
    const [pendingAutoGen, setPendingAutoGen] = useState(false);
    const autoGenRef = useRef(false);

    const onFileChange = (f, err) => {
        if (err) {
            toast.error(err);
            return;
        }
        setFile(f);
        // User explicitly picked a new file — drop any pending OAuth file.
        clearPendingUpload();
    };

    const balance = user?.credits ?? 0;
    const unlimited = !!user?.is_unlimited;
    const outOfCredits = !!user && !unlimited && balance <= 0;
    const isReady = !!file && !working;

    const handleGenerate = useCallback(
        async (overrideFile) => {
            const f = overrideFile || file;
            if (!f) return;

            if (!user) {
                // Stash the photo, kick off Google sign-in. On return,
                // /upload mount restores the file + auto-clicks generate.
                track("generate_click_signed_out");
                const ok = await savePendingUpload(f);
                if (!ok) {
                    toast.error(
                        "图片太大了，请使用 4 MB 以内的照片，或先登录后再上传。",
                    );
                    return;
                }
                startSignIn();
                return;
            }

            if (outOfCredits) {
                toast.error("免费照片已用完，购买套餐继续使用。");
                return;
            }

            setWorking(true);
            track("generate_start", { preset_id: PRESET_ID });

            const form = new FormData();
            form.append("file", f);
            form.append("preset_id", PRESET_ID);

            try {
                const { data: started } = await api.post("/edit", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 180000,
                });
                const jobId = started.job_id;
                await refresh();

                const POLL_MS = 2500;
                const MAX_POLLS = 80;
                let result = null;
                for (let i = 0; i < MAX_POLLS; i++) {
                    await new Promise((r) => setTimeout(r, POLL_MS));
                    try {
                        const { data } = await api.get(`/edit/${jobId}`, {
                            timeout: 90000,
                        });
                        if (data.status === "done") {
                            result = data;
                            break;
                        }
                        if (data.status === "error") {
                            const e = new Error(
                                data.detail || "处理失败，请稍后再试。",
                            );
                            e.serverError = true;
                            throw e;
                        }
                    } catch (pollErr) {
                        if (pollErr?.serverError) throw pollErr;
                        if (pollErr?.response?.status === 404 && result) break;
                        if (pollErr?.response?.data?.detail) throw pollErr;
                    }
                }
                if (!result) {
                    throw new Error("AI 处理超时，请稍后再试。");
                }

                await refresh();
                const originalBlob = URL.createObjectURL(f);
                const editedDataUrl = `data:${result.mime};base64,${result.image_b64}`;
                track("generate_success", {
                    preset_id: PRESET_ID,
                    edit_id: result.job_id,
                });
                clearPendingUpload();
                navigate("/result", {
                    state: {
                        editId: result.job_id,
                        presetId: result.preset_id,
                        editedUrl: editedDataUrl,
                        originalUrl: originalBlob,
                    },
                    replace: true,
                });
            } catch (err) {
                const status = err?.response?.status;
                const msg =
                    err?.response?.data?.detail ||
                    err?.message ||
                    "出错了，请稍后再试。";
                if (status === 401) {
                    toast.error("请重新登录。");
                    startSignIn();
                } else if (status === 402) {
                    await refresh();
                } else {
                    toast.error(msg);
                }
                track("generate_error", {
                    preset_id: PRESET_ID,
                    message: msg,
                });
            } finally {
                setWorking(false);
                await refresh();
            }
        },
        [file, user, outOfCredits, refresh, navigate, startSignIn],
    );

    // After OAuth returns, restore the pending file and auto-generate.
    useEffect(() => {
        if (loading) return;
        if (!user) return;
        if (autoGenRef.current) return;
        (async () => {
            const restored = await loadPendingUpload();
            if (!restored) return;
            autoGenRef.current = true;
            setFile(restored);
            // Small delay so the UI shows the file before kicking off.
            setTimeout(() => {
                handleGenerate(restored);
            }, 250);
        })();
    }, [loading, user, handleGenerate]);

    // Same-page email-OTP sign-in path: pendingAutoGen flips true
    // synchronously in onSuccess, but `user` only becomes truthy on the
    // NEXT render (after React flushes setUser). This effect re-runs
    // then, capturing the fresh handleGenerate + user, and fires the
    // generate call exactly once.
    useEffect(() => {
        if (loading) return;
        if (!user) return;
        if (!pendingAutoGen) return;
        if (!file) return;
        if (autoGenRef.current) return;
        autoGenRef.current = true;
        setPendingAutoGen(false);
        // Microtask delay so the unlimited-badge / credits paint first.
        setTimeout(() => {
            handleGenerate(file);
        }, 100);
    }, [loading, user, pendingAutoGen, file, handleGenerate]);

    return (
        <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col font-cn">
            <LoadingOverlay show={working} />
            <header
                className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/5"
                data-testid="upload-header"
            >
                <div className="max-w-3xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="back-home"
                        className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
                    >
                        <ArrowLeftIcon size={16} weight="bold" /> 返回
                    </Link>
                    <span className="font-cn font-bold tracking-tight">
                        男人照变帅
                    </span>
                    {user ? (
                        unlimited ? (
                            <span
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white text-black rounded-full px-2.5 py-1"
                                data-testid="unlimited-badge"
                            >
                                无限
                            </span>
                        ) : (
                            <span
                                className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-white/[0.05] border border-white/10 rounded-full px-2.5 py-1"
                                data-testid="credit-counter"
                            >
                                <CoinsIcon size={12} weight="fill" />
                                <span className="tabular-nums">{balance}</span>
                                <span className="text-white/40">张</span>
                            </span>
                        )
                    ) : (
                        <span className="w-12" />
                    )}
                </div>
            </header>

            <main className={`flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-6 sm:py-10 space-y-6 ${!user && file && !working ? "pb-12" : "pb-44"}`}>
                {user && outOfCredits ? (
                    <BuyCreditsCard source="upload_page" />
                ) : null}

                <motion.section
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <h1
                        className="font-cn font-bold tracking-tight mb-2"
                        style={{ fontSize: "clamp(28px, 7vw, 40px)" }}
                    >
                        上传你的照片
                    </h1>
                    <p className="text-sm text-white/55 mb-6">
                        清晰单人正面照效果最好。
                    </p>
                    <FileUploader file={file} onChange={onFileChange} />
                    <p
                        data-testid="upload-tip"
                        className="text-xs text-white/75 mt-3"
                    >
                        建议：清晰单人正面照，避免合照、墨镜或低分辨率。
                    </p>
                </motion.section>

                {file && !user && !loading ? (
                    <div className="space-y-3" data-testid="signed-out-auth">
                        {showEmailSignIn ? (
                            <EmailSignIn
                                onSuccess={() => {
                                    // Synchronous state flips ONLY — the
                                    // dedicated useEffect above fires
                                    // handleGenerate once `user` flushes
                                    // on the next render. No sessionStorage
                                    // round-trip, no race.
                                    setShowEmailSignIn(false);
                                    setPendingAutoGen(true);
                                }}
                                onCancel={() => setShowEmailSignIn(false)}
                            />
                        ) : (
                            <>
                                <p className="text-xs text-white/40 text-center px-2">
                                    登录后即可生成 · Sign in to generate
                                </p>
                                {/* Email button (TOP) — same white-pill
                                    style as the Google button so both
                                    read as equal choices. */}
                                <button
                                    type="button"
                                    onClick={() => setShowEmailSignIn(true)}
                                    data-testid="open-email-signin"
                                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold bg-white text-black hover:bg-white/90 active:scale-[0.98] transition-all"
                                >
                                    <EnvelopeSimpleIcon
                                        size={18}
                                        weight="bold"
                                    />
                                    使用邮箱登录 / Sign in with email
                                </button>
                                <div className="flex items-center gap-3">
                                    <span className="flex-1 h-px bg-white/10" />
                                    <span className="text-[11px] uppercase tracking-[0.18em] text-white/35">
                                        或 · OR
                                    </span>
                                    <span className="flex-1 h-px bg-white/10" />
                                </div>
                                {/* Google button (BOTTOM) */}
                                <GoogleSignInButton
                                    testId="google-signin-auth-block"
                                    label="使用 Google 登录 / Sign in with Google"
                                    className="w-full"
                                />
                            </>
                        )}
                    </div>
                ) : null}
            </main>

            {/* Bottom action bar — hidden when signed-out + file selected,
                because the body auth panel becomes the primary CTA. */}
            {!(!user && file && !working) ? (
            <div
                className="fixed bottom-0 inset-x-0 z-50 border-t border-white/5 backdrop-blur-xl bg-[#0A0A0C]/85"
                style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
                data-testid="action-bar"
            >
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4">
                    <button
                        type="button"
                        disabled={!isReady}
                        onClick={() => handleGenerate()}
                        data-testid="generate-button"
                        className={[
                            "w-full inline-flex items-center justify-center gap-2",
                            "rounded-full py-4 text-base font-bold transition-all",
                            isReady
                                ? "bg-white text-black hover:bg-white/90 active:scale-[0.99]"
                                : "bg-white/10 text-white/40 cursor-not-allowed",
                        ].join(" ")}
                    >
                        {working ? (
                            <>
                                <CircleNotchIcon
                                    size={18}
                                    weight="bold"
                                    className="animate-spin"
                                />
                                AI 正在生成…（约 30-60 秒）
                            </>
                        ) : !user && file ? (
                            <>
                                <GoogleLogoIcon size={18} weight="bold" />
                                登录 Google 查看结果
                            </>
                        ) : (
                            <>
                                <LightningIcon size={18} weight="fill" />
                                生成我的照片
                            </>
                        )}
                    </button>
                    <p className="mt-2 text-center text-[11px] text-white/40">
                        {user
                            ? "保留身份特征 · 自然不假"
                            : "免费看看你能变多帅｜首张照片免费"}
                    </p>
                </div>
            </div>
            ) : null}
        </div>
    );
}
